<template>
    <div class="sb-page">
      <!-- Header banner, lepas dari layout admin -->
      <header class="sb-header">
        <div class="sb-header__grid" aria-hidden="true"></div>
        <div class="sb-header__inner">
          <div class="sb-header__top">
            <div class="sb-brand">
              <div class="sb-brand__mark">
                <v-icon size="22" color="#E69F00">mdi-domain</v-icon>
              </div>
              <div>
                <h1 class="sb-brand__title">Dashboard SIMPELBANG</h1>
                <p class="sb-brand__subtitle">Ringkasan realisasi pembangunan, deviasi fisik, dan progres keuangan.</p>
              </div>
            </div>

            <div class="sb-filters">
              <div class="sb-filter sb-filter--year">
                <label>Tahun</label>
                <v-autocomplete
                  v-model="selectedYear"
                  :items="yearOptions"
                  dense
                  outlined
                  single-line
                  hide-details
                  class="sb-filter__input"
                  @change="handleYearChange" />
              </div>
              <div class="sb-filter sb-filter--month">
                <label>Bulan</label>
                <v-autocomplete
                  v-model="selectedMonth"
                  :items="monthOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  class="sb-filter__input"
                  @change="handleMonthChange" />
              </div>
              <router-link to="/login" class="sb-login-btn">
                <v-icon size="16" color="#0B2440">mdi-login-variant</v-icon>
                <span>Masuk</span>
              </router-link>
            </div>
          </div>

          <!-- Rel bulan: pintasan cepat ganti bulan + peta progres tahun anggaran (blueprint style) -->
          <div class="month-rail" role="tablist" aria-label="Pintasan pilih bulan pada tahun anggaran">
            <button
              v-for="item in monthRailItems"
              :key="item.value"
              type="button"
              class="month-rail__seg"
              :class="{ 'is-current': item.value === selectedMonth, 'has-data': item.hasData }"
              :aria-pressed="item.value === selectedMonth"
              :title="item.hasData ? `${item.short}: sudah ada data, klik untuk lihat` : `${item.short}: klik untuk lihat`"
              @click="selectMonthFromRail(item.value)">
              <span class="month-rail__label">{{ item.short }}</span>
            </button>
          </div>
        </div>
      </header>

      <!-- Scroll di inner wrapper supaya header full-bleed (scrollbar
           classic 10px tidak "memotong" kanan header). -->
      <div class="sb-scroll">
      <main class="sb-content">
        <v-alert
          v-if="errorMessage"
          type="error"
          dense
          text
          class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <!-- Kartu metrik -->
        <div class="metric-grid">
          <div
            v-for="item in metricCardsPrimary"
            :key="item.key"
            class="metric-card"
            :style="{ '--metric-accent': item.color }">
            <span class="metric-card__corner metric-card__corner--tl"></span>
            <span class="metric-card__corner metric-card__corner--br"></span>

            <v-icon class="metric-card__ghost" :color="item.color" size="72">{{ item.icon }}</v-icon>

            <div class="metric-card__head">
              <div
                class="metric-icon-box"
                :style="{ background: hexToRgba(item.color, 0.12) }">
                <v-icon size="18" :color="item.color">{{ item.icon }}</v-icon>
              </div>
              <div
                v-if="item.trend"
                class="metric-trend"
                :class="item.trend === 'down' ? 'metric-trend--down' : 'metric-trend--up'">
                <v-icon size="12" :color="item.trend === 'down' ? '#D55E00' : '#009E73'">
                  {{ item.trend === 'down' ? 'mdi-arrow-down' : 'mdi-arrow-up' }}
                </v-icon>
              </div>
            </div>

            <div class="metric-card__label">{{ item.label }}</div>

            <div
              v-if="loadingSummaryMurni || loadingSummary || simpelbangGrafikLoading"
              class="metric-value">
              <v-skeleton-loader type="text" width="80%" height="30px" />
            </div>
            <div v-else class="metric-value" :title="item.value">{{ item.value }}</div>

            <div v-if="item.description" class="metric-sub">{{ item.description }}</div>
          </div>
        </div>

        <!-- Ringkasan jumlah program/kegiatan/sub kegiatan (rad/jumlah_sub_kegiatan) -->
        <div class="jml-stats-head">
          <span class="jml-stats-head__label">Ringkasan Program &amp; Kegiatan</span>
          <span class="jml-stats-head__badge">{{ dpaStatusLabel }}</span>
        </div>
        <div class="jml-stats">
          <div
            v-for="item in jumlahSubKegiatanItems"
            :key="item.key"
            class="jml-stat"
            :style="{ '--jml-accent': item.color }">
            <span class="jml-stat__corner jml-stat__corner--tl"></span>
            <span class="jml-stat__corner jml-stat__corner--br"></span>

            <v-icon class="jml-stat__ghost" :color="item.color" size="52">{{ item.icon }}</v-icon>

            <div class="jml-stat__icon" :style="{ background: hexToRgba(item.color, 0.12) }">
              <v-icon size="16" :color="item.color">{{ item.icon }}</v-icon>
            </div>
            <div class="jml-stat__body">
              <div v-if="jumlahSubKegiatanLoading" class="jml-stat__value">
                <v-skeleton-loader type="text" width="46px" height="22px" />
              </div>
              <div v-else class="jml-stat__value">{{ item.value }}</div>
              <div class="jml-stat__label">{{ item.label }}</div>
            </div>
          </div>
        </div>

        <!-- Chart -->
        <div class="section-label">
          <span class="section-label__text">Grafik Progres Bulanan</span>
          <span class="section-label__meta">Tahun {{ selectedYear }}</span>
        </div>

        <v-alert
          v-if="simpelbangGrafikError"
          type="warning"
          dense
          text
          class="mb-4">
          {{ simpelbangGrafikError }}
        </v-alert>

        <div class="chart-stack">
          <section class="panel">
            <div class="panel__head">
              <div class="panel__heading">
                <div class="panel__icon panel__icon--teal">
                  <v-icon size="17" color="#009E73">mdi-chart-timeline-variant</v-icon>
                </div>
                <div>
                  <div class="panel__title">Grafik Fisik</div>
                  <div class="panel__subtitle">Target vs realisasi fisik per bulan (%)</div>
                </div>
              </div>
            </div>

            <div v-if="simpelbangGrafikLoading" class="chart-loading">
              <v-progress-circular indeterminate color="#0072B2" size="32" width="3" />
              <div class="hint-text mt-2">Memuat grafik fisik…</div>
            </div>
            <apexchart
              v-else-if="simpelbangHasGrafikData"
              type="line"
              :height="300"
              :options="simpelbangFisikChartOptions"
              :series="simpelbangFisikSeries" />
            <div v-else class="chart-empty">
              <v-icon color="#c3ccd6" size="30" class="mb-1">mdi-chart-line</v-icon>
              <div class="hint-text">Belum ada data grafik fisik</div>
            </div>
          </section>

          <section class="panel">
            <div class="panel__head">
              <div class="panel__heading">
                <div class="panel__icon panel__icon--gold">
                  <v-icon size="17" color="#E69F00">mdi-finance</v-icon>
                </div>
                <div>
                  <div class="panel__title">Grafik Keuangan</div>
                  <div class="panel__subtitle">Anggaran, kas, dan realisasi SP2D per bulan</div>
                </div>
              </div>
            </div>

            <div v-if="simpelbangGrafikLoading" class="chart-loading">
              <v-progress-circular indeterminate color="#0072B2" size="32" width="3" />
              <div class="hint-text mt-2">Memuat grafik keuangan…</div>
            </div>
            <apexchart
              v-else-if="simpelbangHasGrafikData"
              type="line"
              :height="300"
              :options="simpelbangKeuanganChartOptions"
              :series="simpelbangKeuanganSeries" />
            <div v-else class="chart-empty">
              <v-icon color="#c3ccd6" size="30" class="mb-1">mdi-chart-areaspline</v-icon>
              <div class="hint-text">Belum ada data grafik keuangan</div>
            </div>
          </section>
        </div>

        <!-- Tabel rekap bulanan — header mengikuti pola contoh Nuxt (judul + badge) -->
        <section class="panel table-panel">
          <div class="panel__head table-panel__head">
            <div>
              <h3 class="table-panel__title">Ringkasan Per Bulan</h3>
            </div>
            <span class="table-panel__badge">Rekap</span>
          </div>
          <div class="table-wrap">
            <table class="monthly-table">
              <thead>
                <tr>
                  <th>Bulan</th>
                  <th>Target</th>
                  <th>Realisasi</th>
                  <th>Deviasi</th>
                  <th>Anggaran Murni</th>
                  <th>Anggaran P1</th>
                  <th style="min-width: 200px;">Anggaran Perubahan</th>
                  <th style="min-width: 300px;">Anggaran Kas Perbulan (Rp)</th>
                  <th style="min-width: 350px;">Persentase SP2D terhadap Pagu Anggaran</th>
                  <th style="min-width: 300px;">Realisasi Keuangan/SP2D (Rp)</th>
                  <th style="min-width: 350px;">Persentase SP2D terhadap Kas Perbulan</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in monthlyTableRows"
                  :key="`${row.bulan}-${idx}`"
                  :class="{ 'is-current-row': (idx + 1) === selectedMonth }">
                  <td class="cell-month">{{ row.bulan }}</td>
                  <td class="cell-num">{{ row.target }}</td>
                  <td class="cell-num">{{ row.realisasi }}</td>
                  <td class="cell-num">
                    <span
                      class="deviasi-pill"
                      :class="row.deviasi.trim().startsWith('-') ? 'deviasi-pill--down' : (row.deviasi === '-' ? 'deviasi-pill--flat' : 'deviasi-pill--up')">
                      {{ row.deviasi }}
                    </span>
                  </td>
                  <td class="cell-num">{{ row.anggaranMurni }}</td>
                  <td class="cell-num">{{ row.anggaranP1 }}</td>
                  <td class="cell-num">{{ row.anggaranPerubahan }}</td>
                  <td class="cell-num">{{ row.anggaranKasPerBulan }}</td>
                  <td class="cell-num">{{ row.persentaseSp2dPagu }}</td>
                  <td class="cell-num">{{ row.realisasiKeuanganSp2d }}</td>
                  <td class="cell-num">{{ row.persentaseSp2dKasPerBulan }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>
      </div>
    </div>
  </template>

  <script>
  import VueApexCharts from 'vue-apexcharts'

  // -- API SIMPELBANG (public, sama seperti contoh) --
  const SIMPELBANG_BASE_URL = 'https://api.diskominfo.klaten.go.id/simpelbang2024/rad'
  const SIMPELBANG_AUTH_HEADERS = {
    Authorization: 'Basic b2ZmaWNlOk9uRGVtYW5kT2ZmaWNlU2lzdGVtQXBwcw==',
    'x-api-key': 'ee523544e8a644946058b0b30fa4db9c7e283cb6+675bcec6ee20543fba77baa8c38bd04b2e6c7204'
  }

  const PRIMARY_METRIC_KEYS = [
    'grand_total_anggaran',
    'total_realisasi_keuangan',
    'grand_total_total_target_fisik',
    'grand_total_deviasi'
  ]

  const MONTHS = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ]

  export default {
    name: 'DashboardPublicSimpelbang',
    components: {
      apexchart: VueApexCharts
    },
    data() {
      return {
        loading: false,
        loadingSummaryMurni: false,
        loadingSummary: false,
        errorMessage: '',

        simpelbangGrafikLoading: false,
        simpelbangGrafikError: '',
        simpelbangGrafikSummary: [],

        summary: null,
        summaryMurni: null,

        jumlahSubKegiatanLoading: false,
        jumlahSubKegiatan: null,

        selectedYear: new Date().getFullYear(),
        selectedMonth: new Date().getMonth() + 1,

        dpaDateConfig: null,
        dpaStatus: 'murni',

        metricFields: [
          { key: 'grand_total_anggaran', label: 'Total Anggaran Kumulatif', icon: 'mdi-cash-multiple', color: '#0072B2' },
          { key: 'grand_total_total_target_fisik', label: 'Target Fisik Bulan Terpilih', icon: 'mdi-target', color: '#009E73' },
          { key: 'grand_total_deviasi', label: 'Deviasi Fisik Bulan Terpilih', icon: 'mdi-chart-line', color: '#009E73' },
          { key: 'total_realisasi_keuangan', label: 'Realisasi Keuangan Kumulatif', icon: 'mdi-wallet-outline', color: '#E69F00' }
        ]
      }
    },
    computed: {
      selectedMonthLabel() {
        return (this.monthOptions.find((item) => item.value === this.selectedMonth) || {}).label || '-'
      },
      yearOptions() {
        const startYear = 2024
        const endYear = new Date().getFullYear()
        const years = []
        for (let year = endYear; year >= startYear; year -= 1) years.push(year)
        return years
      },
      monthOptions() {
        return MONTHS.map((label, idx) => ({ label, value: idx + 1 }))
      },
      metricCardsPrimary() {
        return PRIMARY_METRIC_KEYS.map((key) => {
          const field = this.metricFields.find((f) => f.key === key)
          return field ? this.buildMetricCard(field) : null
        }).filter(Boolean)
      },
      simpelbangGrafikRows() {
        const sourceRows = Array.isArray(this.simpelbangGrafikSummary) ? this.simpelbangGrafikSummary : []
        const byMonth = new Map()
        sourceRows
          .slice()
          .sort((a, b) => Number(a?.bulan_anggaran) - Number(b?.bulan_anggaran))
          .forEach((row) => {
            const month = Number(row?.bulan_anggaran)
            if (!Number.isFinite(month) || month < 1 || month > 12) return
            byMonth.set(month, row)
          })
        return this.monthOptions.map((item) => {
          const row = byMonth.get(item.value) || {}
          return {
            month: item.value,
            anggaran_murni: row.anggaran_murni ?? null,
            anggaran_p1: row.anggaran_p1 ?? null,
            anggaran_perubahan: row.anggaran_perubahan ?? null,
            target_fisik: row.target_fisik ?? null,
            realisasi_fisik: row.realisasi_fisik ?? null,
            deviasi_fisik: row.deviasi_fisik ?? null,
            kas_per_bulan: row.kas_per_bulan ?? null,
            persentase_sp2d_pagu: row.persentase_sp2d_pagu ?? null,
            realisasi_keuangan_sp2d: row.realisasi_keuangan_sp2d ?? null,
            persentase_sp2d_kas_bulanan: row.persentase_sp2d_kas_bulanan ?? null
          }
        })
      },
      simpelbangHasGrafikData() {
        return this.simpelbangGrafikRows.length === this.monthOptions.length
      },
      simpelbangGrafikCategories() {
        return this.simpelbangGrafikRows.map((row) => {
          const found = this.monthOptions.find((item) => item.value === row.month)
          return found ? found.label : `Bulan ${row.month}`
        })
      },
      simpelbangFisikSeries() {
        return [
          { name: 'Target', data: this.simpelbangGrafikRows.map((row) => this.toNumberOrNull(row.target_fisik)) },
          { name: 'Realisasi', data: this.simpelbangGrafikRows.map((row) => this.toNumberOrNull(row.realisasi_fisik)) },
          { name: 'Deviasi', data: this.simpelbangGrafikRows.map((row) => this.toNumberOrNull(row.deviasi_fisik)) }
        ]
      },
      simpelbangFisikChartOptions() {
        return {
          chart: { type: 'line', toolbar: { show: true }, zoom: { enabled: true }, fontFamily: 'inherit' },
          stroke: { width: [3, 3, 3], curve: 'smooth' },
          markers: { size: 4 },
          dataLabels: { enabled: false },
          xaxis: { categories: this.simpelbangGrafikCategories, title: { text: 'Bulan', style: { fontSize: '12px' } } },
          yaxis: {
            title: { text: 'Persentase (%)', style: { fontSize: '12px' } },
            labels: { formatter: (val) => (Number.isFinite(Number(val)) ? `${Number(val).toFixed(2)}%` : '') }
          },
          colors: ['#0072B2', '#E69F00', '#CC79A7'],
          tooltip: {
            shared: true,
            intersect: false,
            y: { formatter: (val) => (Number.isFinite(Number(val)) ? `${Number(val).toFixed(2)}%` : '-') }
          },
          legend: { position: 'bottom', fontSize: '12px' }
        }
      },
      simpelbangKeuanganSeries() {
        const makeData = (key) => this.simpelbangGrafikRows.map((row) => this.toNumberOrNull(row[key]))
        return [
          { name: 'Anggaran Murni', type: 'column', yAxisIndex: 0, data: makeData('anggaran_murni') },
          { name: 'Anggaran Kas Perbulan (Rp)', type: 'column', yAxisIndex: 0, data: makeData('kas_per_bulan') },
          { name: 'Persentase SP2D terhadap Pagu', type: 'column', yAxisIndex: 1, data: makeData('persentase_sp2d_pagu') },
          { name: 'Realisasi Keuangan/SP2D (Rp)', type: 'column', yAxisIndex: 0, data: makeData('realisasi_keuangan_sp2d') },
          { name: 'Persentase SP2D terhadap Kas Perbulan', type: 'column', yAxisIndex: 1, data: makeData('persentase_sp2d_kas_bulanan') }
        ]
      },
      simpelbangKeuanganChartOptions() {
        const percentValues = [
          ...this.simpelbangGrafikRows.map((row) => this.toNumberOrZero(row.persentase_sp2d_pagu)),
          ...this.simpelbangGrafikRows.map((row) => this.toNumberOrZero(row.persentase_sp2d_kas_bulanan))
        ]
        const maxPercent = percentValues.length ? Math.max(...percentValues) : 0
        const percentAxisMax = Math.max(10, Math.ceil(maxPercent / 10) * 10)
        return {
          chart: { type: 'line', stacked: false, toolbar: { show: true }, zoom: { enabled: true }, fontFamily: 'inherit' },
          stroke: {
            width: this.simpelbangKeuanganSeries.map((s) => (s.type === 'line' ? 3 : 0)),
            curve: 'smooth'
          },
          plotOptions: { bar: { columnWidth: '50%', borderRadius: 2 } },
          dataLabels: { enabled: false },
          xaxis: { categories: this.simpelbangGrafikCategories, title: { text: 'Bulan', style: { fontSize: '12px' } } },
          yaxis: [
            {
              title: { text: 'Nominal (Rp)', style: { fontSize: '12px' } },
              labels: {
                formatter: (val) => (Number.isFinite(Number(val)) ? new Intl.NumberFormat('id-ID').format(Math.round(Number(val))) : '')
              }
            },
            {
              opposite: true,
              title: { text: 'Persentase (%)', style: { fontSize: '12px' } },
              min: 0,
              max: percentAxisMax,
              tickAmount: Math.max(2, Math.min(8, Math.round(percentAxisMax / 10))),
              forceNiceScale: true,
              labels: { formatter: (val) => (Number.isFinite(Number(val)) ? `${Number(val).toFixed(2)}%` : '') }
            }
          ],
          colors: ['#0072B2', '#56B4E9', '#E69F00', '#009E73', '#CC79A7'],
          tooltip: {
            shared: true,
            intersect: false,
            y: {
              formatter: (val, context) => {
                const n = Number(val)
                if (!Number.isFinite(n)) return '-'
                const seriesName = context && context.w && context.w.config && context.w.config.series[context.seriesIndex]
                  ? context.w.config.series[context.seriesIndex].name
                  : ''
                if (seriesName.toLowerCase().includes('persentase')) return `${n.toFixed(2)}%`
                return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)
              }
            }
          },
          legend: { position: 'bottom', fontSize: '12px' }
        }
      },
      monthlyTableRows() {
        return this.simpelbangGrafikRows.map((row) => ({
          bulan: MONTHS[Math.max(0, Math.min(11, row.month - 1))] || '-',
          target: this.formatPercent(row.target_fisik),
          realisasi: this.formatPercent(row.realisasi_fisik),
          deviasi: this.formatPercent(row.deviasi_fisik),
          anggaranMurni: this.formatCurrency(row.anggaran_murni),
          anggaranP1: this.formatCurrency(row.anggaran_p1),
          anggaranPerubahan: this.formatCurrency(row.anggaran_perubahan),
          anggaranKasPerBulan: this.formatCurrency(row.kas_per_bulan),
          persentaseSp2dPagu: this.formatPercent(row.persentase_sp2d_pagu),
          realisasiKeuanganSp2d: this.formatCurrency(row.realisasi_keuangan_sp2d),
          persentaseSp2dKasPerBulan: this.formatPercent(row.persentase_sp2d_kas_bulanan)
        }))
      },
      // -- Anggaran bulan terpilih, sumbernya SAMA PERSIS dengan tabel (grafik_summary) --
      // supaya kartu "Total Anggaran Kumulatif" selalu match dengan angka di tabel:
      // pakai anggaran_perubahan kalau sudah ada, kalau belum pakai anggaran_p1,
      // kalau belum ada juga baru fallback ke anggaran_murni.
      selectedMonthAnggaran() {
        const row = this.simpelbangGrafikRows.find((r) => r.month === this.selectedMonth)
        if (!row) return null
        // API pakai "0.00" sebagai placeholder buat anggaran_p1/anggaran_perubahan
        // SEBELUM masa itu benar-benar aktif (dikonfirmasi dari contoh balikan API:
        // anggaran_p1 Januari = "0.00", baru berisi angka asli mulai Februari) —
        // jadi 0 harus dianggap "belum ada", bukan cuma null/"" yang dianggap kosong.
        const perubahan = this.toNumberOrNull(row.anggaran_perubahan)
        if (perubahan !== null && perubahan > 0) return row.anggaran_perubahan
        const p1 = this.toNumberOrNull(row.anggaran_p1)
        if (p1 !== null && p1 > 0) return row.anggaran_p1
        return row.anggaran_murni ?? null
      },
      // -- Presentasional saja: rel bulan (signature header), tidak memengaruhi data/logic lain --
      monthRailItems() {
        return this.monthOptions.map((item) => {
          const row = this.simpelbangGrafikRows.find((r) => r.month === item.value)
          const hasData = !!row && this.toNumberOrNull(row.realisasi_fisik) !== null
          return {
            value: item.value,
            short: item.label.slice(0, 3),
            hasData
          }
        })
      },
      // -- Label filter dpa_status yang lagi dipakai untuk data jumlah_sub_kegiatan --
      dpaStatusLabel() {
        const map = { murni: 'Anggaran Murni', p1: 'Anggaran P1', perubahan: 'Anggaran Perubahan' }
        return map[this.dpaStatus] || 'Anggaran Murni'
      },
      // -- Presentasional: 3 angka ringkas dari rad/jumlah_sub_kegiatan --
      jumlahSubKegiatanItems() {
        const data = this.jumlahSubKegiatan || {}
        return [
          { key: 'total_program', label: 'Total Program', icon: 'mdi-format-list-bulleted', color: '#0072B2', value: this.formatNumber(data.total_program) },
          { key: 'total_kegiatan', label: 'Total Kegiatan', icon: 'mdi-briefcase-outline', color: '#E69F00', value: this.formatNumber(data.total_kegiatan) },
          { key: 'total_sub_kegiatan', label: 'Total Sub Kegiatan', icon: 'mdi-format-list-checks', color: '#009E73', value: this.formatNumber(data.total_sub_kegiatan) }
        ]
      }
    },
    mounted() {
      this.handleFilterChange()
      // Lock html+body: Vuetify set `html { overflow-y: scroll }` yang selalu
      // reservasi gutter 10px. Lock body saja tidak cukup — scroll pindah ke html.
      document.documentElement.classList.add('sb-page-active')
      document.body.classList.add('sb-page-active')
      // <router-view style="padding-left: 2px"> di App.vue nempel ke root $el.
      // Bersihkan supaya overlay benar-benar edge-to-edge.
      if (this.$el) this.$el.style.paddingLeft = '0'
    },
    beforeDestroy() {
      document.documentElement.classList.remove('sb-page-active')
      document.body.classList.remove('sb-page-active')
    },
    methods: {
      handleYearChange() {
        this.handleFilterChange({ withGrafik: true })
      },
      handleMonthChange() {
        this.handleFilterChange({ withGrafik: false })
      },
      // Pintasan dari rel bulan di header — set bulan lalu panggil ulang logic yang sama persis dengan dropdown Bulan
      selectMonthFromRail(monthValue) {
        if (this.selectedMonth === monthValue) return
        this.selectedMonth = monthValue
        this.handleMonthChange()
      },
      async handleFilterChange({ withGrafik = true } = {}) {
        this.loading = true
        this.errorMessage = ''
        await this.fetchDataDpaConfig()

        const promises = [this.fetchDataMurni(), this.fetchData(), this.fetchJumlahSubKegiatan()]
        if (withGrafik) promises.push(this.fetchSimpelbangGrafikSummary())

        await Promise.all(promises)
        this.loading = false
      },
      async fetchDataDpaConfig() {
        try {
          const query = new URLSearchParams({ tahun: String(this.selectedYear) })
          const response = await fetch(`${SIMPELBANG_BASE_URL}/dpa?${query.toString()}`, {
            method: 'GET',
            headers: SIMPELBANG_AUTH_HEADERS
          })
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const payload = await response.json()
          const dpaData = payload && payload.results && payload.results.data
          this.dpaDateConfig = Array.isArray(dpaData) && dpaData.length > 0 ? dpaData[0] : null
          this.dpaStatus = this.resolveDpaStatus()
        } catch (error) {
          this.dpaDateConfig = null
          this.dpaStatus = 'murni'
          this.errorMessage = `Gagal mengambil data: ${error.message || 'Unknown error'}`
        }
      },
      async fetchSimpelbangGrafikSummary() {
        this.simpelbangGrafikLoading = true
        this.simpelbangGrafikError = ''
        try {
          const query = new URLSearchParams({ tahun_anggaran: String(this.selectedYear), limit: 1000000 })
          const response = await fetch(`${SIMPELBANG_BASE_URL}/grafik_summary?${query.toString()}`, {
            method: 'GET',
            headers: SIMPELBANG_AUTH_HEADERS
          })
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const payload = await response.json()
          const rows =
            (Array.isArray(payload && payload.results) && payload.results) ||
            (Array.isArray(payload && payload.results && payload.results.data) && payload.results.data) ||
            (Array.isArray(payload && payload.data) && payload.data) ||
            (Array.isArray(payload) && payload) ||
            []
          this.simpelbangGrafikSummary = rows
        } catch (error) {
          this.simpelbangGrafikSummary = []
          this.simpelbangGrafikError = `Gagal memuat grafik SIMPELBANG: ${error.message || 'Unknown error'}`
        } finally {
          this.simpelbangGrafikLoading = false
        }
      },
      async fetchDataMurni() {
        this.loadingSummaryMurni = true
        try {
          const query = new URLSearchParams({
            limit: '2000000000000000',
            page: '1',
            search: '',
            tahun_anggaran: String(this.selectedYear),
            is_pengecualian: '1',
            year: String(this.selectedYear),
            month: String(this.selectedMonth),
            dpa_status: 'murni'
          })
          const response = await fetch(`${SIMPELBANG_BASE_URL}/summary_opd_public?${query.toString()}`, {
            method: 'GET',
            headers: SIMPELBANG_AUTH_HEADERS
          })
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const payload = await response.json()
          this.summaryMurni = (payload && payload.results) || {}
        } catch (error) {
          this.summaryMurni = null
          this.errorMessage = `Gagal mengambil data: ${error.message || 'Unknown error'}`
        } finally {
          this.loadingSummaryMurni = false
        }
      },
      async fetchData() {
        this.loadingSummary = true
        try {
          const query = new URLSearchParams({
            limit: '2000000000000000',
            page: '1',
            search: '',
            tahun_anggaran: String(this.selectedYear),
            is_pengecualian: '1',
            year: String(this.selectedYear),
            month: String(this.selectedMonth),
            dpa_status: this.dpaStatus || 'murni'
          })
          const response = await fetch(`${SIMPELBANG_BASE_URL}/summary_opd_public?${query.toString()}`, {
            method: 'GET',
            headers: SIMPELBANG_AUTH_HEADERS
          })
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const payload = await response.json()
          this.summary = (payload && payload.results) || {}
        } catch (error) {
          this.summary = null
          this.errorMessage = `Gagal mengambil data: ${error.message || 'Unknown error'}`
        } finally {
          this.loadingSummary = false
        }
      },
      async fetchJumlahSubKegiatan() {
        this.jumlahSubKegiatanLoading = true
        try {
          const query = new URLSearchParams({
            limit: '2000000000000000',
            page: '1',
            search: '',
            tahun_anggaran: String(this.selectedYear),
            is_pengecualian: '1',
            year: String(this.selectedYear),
            month: String(this.selectedMonth),
            dpa_status: this.dpaStatus || 'murni'
          })
          const response = await fetch(`${SIMPELBANG_BASE_URL}/jumlah_sub_kegiatan?${query.toString()}`, {
            method: 'GET',
            headers: SIMPELBANG_AUTH_HEADERS
          })
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const payload = await response.json()
          this.jumlahSubKegiatan = (payload && payload.results && payload.results.data) || null
        } catch (error) {
          this.jumlahSubKegiatan = null
          this.errorMessage = `Gagal mengambil data: ${error.message || 'Unknown error'}`
        } finally {
          this.jumlahSubKegiatanLoading = false
        }
      },
      resolveDpaStatus() {
        const selectedMonth = Number(this.selectedMonth)
        const murniMonth = this.parseMonthFromDate(this.dpaDateConfig && this.dpaDateConfig.murni_date)
        const p1Month = this.parseMonthFromDate(this.dpaDateConfig && this.dpaDateConfig.p1_date)
        const perubahanMonth = this.parseMonthFromDate(this.dpaDateConfig && this.dpaDateConfig.perubahan_date)
        if (perubahanMonth !== null && selectedMonth >= perubahanMonth) return 'perubahan'
        if (p1Month !== null && selectedMonth >= p1Month) {
          if (perubahanMonth === null || selectedMonth < perubahanMonth) return 'p1'
        }
        return 'murni'
      },
      parseMonthFromDate(dateString) {
        if (!dateString) return null
        const date = new Date(dateString)
        if (Number.isNaN(date.getTime())) return null
        return date.getMonth() + 1
      },
      buildMetricCard(field) {
        // grand_total_anggaran pakai selectedMonthAnggaran (sumber data sama dengan
        // tabel Ringkasan Per Bulan) supaya angkanya selalu match dengan tabel.
        // Field lain tetap dari this.summary seperti sebelumnya.
        const rawValue = field.key === 'grand_total_anggaran'
          ? this.selectedMonthAnggaran
          : this.summary && this.summary[field.key]

        let color = field.color
        let trend = null
        if (field.key === 'grand_total_deviasi') {
          const deviasi = this.toNumberOrNull(String(rawValue).replace('%', ''))
          if (deviasi !== null && deviasi < 0) {
            trend = 'down'
            color = '#D55E00'
          } else if (deviasi !== null) {
            trend = 'up'
            color = '#009E73'
          }
        }

        return {
          ...field,
          color,
          trend,
          value: this.formatMetricValue(field.key, rawValue),
          description: this.metricDescriptionFor(field.key)
        }
      },
      metricDescriptionFor(key) {
        if (key === 'grand_total_anggaran') return 'Akumulasi anggaran seluruh bulan pada tahun terpilih'
        if (key === 'total_realisasi_keuangan') return 'Nilai realisasi keuangan sampai bulan berjalan'
        if (key === 'grand_total_total_target_fisik') return `${this.selectedMonthLabel} tahun ${this.selectedYear}`
        if (key === 'grand_total_deviasi') return `${this.selectedMonthLabel} tahun ${this.selectedYear}`
        return ''
      },
      formatMetricValue(key, value) {
        if (value === null || value === undefined || value === '') return '-'
        const currencyKeys = ['grand_total_anggaran', 'total_realisasi_keuangan']
        const percentKeys = ['grand_total_total_target_fisik', 'grand_total_deviasi']

        if (currencyKeys.includes(key)) {
          const n = this.toNumberOrNull(value)
          if (n !== null) {
            const showTwoDecimals = Math.abs(n % 1) > 0
            return new Intl.NumberFormat('id-ID', {
              style: 'currency',
              currency: 'IDR',
              minimumFractionDigits: showTwoDecimals ? 2 : 0,
              maximumFractionDigits: showTwoDecimals ? 2 : 0
            }).format(n)
          }
        }
        if (percentKeys.includes(key)) {
          const n = this.toNumberOrNull(String(value).replace('%', ''))
          if (n !== null) {
            return `${new Intl.NumberFormat('id-ID', {
              minimumFractionDigits: Math.abs(n % 1) > 0 ? 2 : 0,
              maximumFractionDigits: 2
            }).format(n)}%`
          }
        }
        const n = this.toNumberOrNull(value)
        return n !== null
          ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(n)
          : String(value)
      },
      toNumberOrNull(value) {
        if (value === null || value === undefined || value === '') return null
        const n = Number(value)
        return Number.isFinite(n) ? n : null
      },
      hexToRgba(hex, alpha) {
        if (!hex) return `rgba(0, 114, 178, ${alpha})`
        const clean = hex.replace('#', '')
        const bigint = parseInt(clean.length === 3
          ? clean.split('').map((c) => c + c).join('')
          : clean, 16)
        const r = (bigint >> 16) & 255
        const g = (bigint >> 8) & 255
        const b = bigint & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      },
      toNumberOrZero(value) {
        const n = this.toNumberOrNull(value)
        return n === null ? 0 : n
      },
      formatPercent(value) {
        const n = this.toNumberOrNull(value)
        if (n === null) return '-'
        return `${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}%`
      },
      formatCurrency(value) {
        const n = this.toNumberOrNull(value)
        if (n === null || n === 0) return '-'
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n)
      },
      formatNumber(value) {
        const n = this.toNumberOrNull(value)
        return n !== null ? new Intl.NumberFormat('id-ID').format(n) : '-'
      }
    }
  }
  </script>

  <style scoped>
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap');

  /* ===== Design tokens ===== */
  .sb-page {
    --sb-navy: #0B2440;
    --sb-navy-2: #0F3157;
    --sb-blue: #0072B2;
    --sb-blue-light: #56B4E9;
    --sb-skyblue: #56B4E9;
    --sb-gold: #E69F00;
    --sb-teal: #009E73;
    --sb-red: #D55E00;
    --sb-green: #009E73;
    --sb-purple: #CC79A7;
    --sb-bg: #EEF2F7;
    --sb-surface: #FFFFFF;
    --sb-border: #E1E7EF;
    --sb-ink: #10233B;
    --sb-muted: #647389;

    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    max-width: 100vw;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding: 0 !important; /* override padding-left:2px dari <router-view> di App.vue */
    background: var(--sb-bg);
    color: var(--sb-ink);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    -webkit-font-smoothing: antialiased;
  }

  .sb-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;
  }

  .sb-page ::v-deep .metric-value,
  .sb-page ::v-deep td.cell-num,
  .sb-page ::v-deep .deviasi-pill {
    font-variant-numeric: tabular-nums;
  }

  /* ===== Header ===== */
  .sb-header {
    position: relative;
    flex-shrink: 0;
    z-index: 5;
    background: linear-gradient(150deg, var(--sb-navy) 0%, var(--sb-navy-2) 62%, #123a68 100%);
    border-bottom: 3px solid var(--sb-gold);
    box-shadow: 0 6px 20px rgba(6, 20, 38, 0.22);
    overflow: hidden;
  }

  /* Blueprint grid motif — nod ke "pembangunan" (konstruksi) */
  .sb-header__grid {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.05) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
    background-size: 34px 34px;
    mask-image: radial-gradient(ellipse at top right, black 0%, transparent 75%);
  }

  .sb-header__inner {
    position: relative;
    max-width: 1400px;
    margin: 0 auto;
    padding: clamp(16px, 2vw, 22px) clamp(18px, 2.4vw, 32px) 0;
  }

  .sb-header__top {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 20px;
    flex-wrap: wrap;
    padding-bottom: clamp(14px, 1.8vw, 18px);
  }

  .sb-brand {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .sb-brand__mark {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(230, 159, 0, 0.14);
    border: 1px solid rgba(230, 159, 0, 0.4);
  }

  .sb-brand__title {
    margin: 0;
    color: #ffffff;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-size: clamp(18px, 1.7vw, 22px);
    font-weight: 800;
    letter-spacing: -0.015em;
  }

  .sb-brand__subtitle {
    margin: 4px 0 0;
    color: rgba(255, 255, 255, 0.68);
    font-size: 12.5px;
    font-weight: 500;
  }

  /* ===== Filters ===== */
  .sb-filters {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
  }

  .sb-filter {
    flex: 0 0 auto;
  }

  .sb-filter--year {
    width: 104px;
  }

  .sb-filter--month {
    width: 156px;
  }

  .sb-filter label {
    display: block;
    margin-bottom: 6px;
    font-size: 10.5px;
    color: rgba(255, 255, 255, 0.65);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .sb-filter__input::v-deep .v-input__slot {
    border-radius: 8px !important;
    min-height: 36px !important;
    background: #ffffff !important;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14) !important;
  }

  .sb-filter__input::v-deep fieldset {
    border-color: transparent !important;
  }

  .sb-filter__input::v-deep input {
    font-size: 13px;
    font-weight: 600;
    color: var(--sb-navy);
  }

  .sb-login-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    background: var(--sb-gold);
    color: var(--sb-navy);
    font-size: 12.5px;
    font-weight: 700;
    text-decoration: none;
    flex-shrink: 0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.14);
    transition: filter 0.15s ease, transform 0.15s ease;
  }

  .sb-login-btn:hover {
    filter: brightness(1.06);
    transform: translateY(-1px);
  }

  .sb-login-btn:active {
    transform: translateY(0);
  }

  /*
   * Halaman ini pakai position:fixed + z-index tinggi supaya tampil di atas layout
   * admin. Tapi dropdown Tahun/Bulan (v-menu) dari Vuetify di-teleport ke <body>
   * dengan z-index bawaan yang lebih rendah, jadi menu-nya kebuka tapi ketutup
   * halaman ini sendiri — makanya klik select "kelihatan" tidak berfungsi.
   * Dinaikkan lewat ::v-deep di bawah.
   */
  .sb-filter__input::v-deep .v-menu__content {
    z-index: 2600 !important;
  }

  /* ===== Month rail (signature element) ===== */
  .month-rail {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    gap: 4px;
    padding: 10px 0 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }

  .month-rail__seg {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    width: 100%;
    box-sizing: border-box;
    padding: 6px 2px 5px;
    margin: 0;
    position: relative;
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    font: inherit;
    -webkit-tap-highlight-color: transparent;
    transition: background 0.15s ease;
  }

  .month-rail__seg:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  .month-rail__seg:focus-visible {
    outline: 2px solid var(--sb-skyblue);
    outline-offset: 1px;
  }

  .month-rail__seg::before {
    content: '';
    position: absolute;
    top: 0;
    left: 8%;
    right: 8%;
    height: 3px;
    border-radius: 2px;
    background: rgba(255, 255, 255, 0.14);
  }

  .month-rail__seg.has-data::before {
    background: var(--sb-teal);
  }

  .month-rail__seg.is-current::before {
    background: var(--sb-gold);
    height: 3px;
    box-shadow: 0 0 0 3px rgba(230, 159, 0, 0.22);
  }

  .month-rail__label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: rgba(255, 255, 255, 0.45);
  }

  .month-rail__seg.has-data .month-rail__label {
    color: rgba(255, 255, 255, 0.72);
  }

  .month-rail__seg.is-current .month-rail__label {
    color: #ffffff;
    font-weight: 800;
  }

  /* ===== Content ===== */
  .sb-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: clamp(18px, 2.2vw, 28px) clamp(18px, 2.4vw, 32px) 60px;
  }

  .section-label {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin: 30px 0 14px;
    padding-left: 12px;
    border-left: 3px solid var(--sb-gold);
  }

  .section-label__text {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    color: var(--sb-navy);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .section-label__meta {
    font-size: 12px;
    color: var(--sb-muted);
    font-weight: 500;
  }

  /* ===== Metric cards ===== */
  .metric-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: 14px;
  }

  .metric-card {
    position: relative;
    background: var(--sb-surface);
    border: 1px solid var(--sb-border);
    border-radius: 12px;
    padding: 18px 18px 16px;
    min-width: 0;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 35, 59, 0.04);
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }

  .metric-card:hover {
    box-shadow: 0 10px 24px rgba(16, 35, 59, 0.1);
    transform: translateY(-1px);
  }

  /* Drafting-style corner marks — blueprint motif echoed from header */
  .metric-card__corner {
    position: absolute;
    width: 14px;
    height: 14px;
    pointer-events: none;
    opacity: 0.55;
  }

  .metric-card__corner--tl {
    top: 8px;
    left: 8px;
    border-top: 2px solid var(--metric-accent, var(--sb-blue));
    border-left: 2px solid var(--metric-accent, var(--sb-blue));
    border-radius: 3px 0 0 0;
  }

  .metric-card__corner--br {
    bottom: 8px;
    right: 8px;
    border-bottom: 2px solid var(--metric-accent, var(--sb-blue));
    border-right: 2px solid var(--metric-accent, var(--sb-blue));
    border-radius: 0 0 3px 0;
  }

  .metric-card__ghost {
    position: absolute;
    right: -8px;
    bottom: -14px;
    opacity: 0.06;
    transform: rotate(-6deg);
  }

  .metric-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    position: relative;
    z-index: 1;
  }

  .metric-icon-box {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .metric-trend {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .metric-trend--up {
    background: rgba(0, 158, 115, 0.14);
  }

  .metric-trend--down {
    background: rgba(213, 94, 0, 0.14);
  }

  .metric-card__label {
    font-size: 12px;
    font-weight: 600;
    color: var(--sb-muted);
    line-height: 1.35;
    margin-bottom: 8px;
    position: relative;
    z-index: 1;
  }

  .metric-value {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: clamp(19px, 1.6vw, 25px);
    font-weight: 800;
    color: var(--sb-navy);
    line-height: 1.2;
    word-break: keep-all;
    overflow-wrap: normal;
    letter-spacing: -0.015em;
    position: relative;
    z-index: 1;
  }

  .metric-sub {
    font-size: 11.5px;
    color: var(--sb-muted);
    line-height: 1.4;
    margin-top: 8px;
    position: relative;
    z-index: 1;
  }

  /* ===== Strip ringkasan jumlah program/kegiatan/sub kegiatan ===== */
  .jml-stats-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 18px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .jml-stats-head__label {
    font-size: 11.5px;
    font-weight: 700;
    color: var(--sb-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .jml-stats-head__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    background: rgba(230, 159, 0, 0.12);
    color: #a06400;
    flex-shrink: 0;
  }

  .jml-stats {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 14px;
    margin-top: 0;
  }

  .jml-stat {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    background: var(--sb-surface);
    border: 1px solid var(--sb-border);
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 0;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(16, 35, 59, 0.04);
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }

  .jml-stat:hover {
    box-shadow: 0 10px 24px rgba(16, 35, 59, 0.1);
    transform: translateY(-1px);
  }

  .jml-stat__corner {
    position: absolute;
    width: 12px;
    height: 12px;
    pointer-events: none;
    opacity: 0.55;
  }

  .jml-stat__corner--tl {
    top: 7px;
    left: 7px;
    border-top: 2px solid var(--jml-accent, var(--sb-blue));
    border-left: 2px solid var(--jml-accent, var(--sb-blue));
    border-radius: 3px 0 0 0;
  }

  .jml-stat__corner--br {
    bottom: 7px;
    right: 7px;
    border-bottom: 2px solid var(--jml-accent, var(--sb-blue));
    border-right: 2px solid var(--jml-accent, var(--sb-blue));
    border-radius: 0 0 3px 0;
  }

  .jml-stat__ghost {
    position: absolute;
    right: -6px;
    bottom: -12px;
    opacity: 0.06;
    transform: rotate(-6deg);
  }

  .jml-stat__icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  .jml-stat__body {
    min-width: 0;
    position: relative;
    z-index: 1;
  }

  .jml-stat__value {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 19px;
    font-weight: 800;
    color: var(--sb-navy);
    line-height: 1.2;
    letter-spacing: -0.01em;
  }

  .jml-stat__label {
    font-size: 11.5px;
    font-weight: 600;
    color: var(--sb-muted);
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ===== Panels (chart & table) ===== */
  .panel {
    background: var(--sb-surface);
    border: 1px solid var(--sb-border);
    border-radius: 12px;
    padding: 18px 20px 20px;
    box-shadow: 0 1px 2px rgba(16, 35, 59, 0.04);
  }

  .panel__heading {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .panel__icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .panel__icon--teal { background: rgba(0, 158, 115, 0.12); }
  .panel__icon--gold { background: rgba(230, 159, 0, 0.14); }
  .panel__icon--blue { background: rgba(0, 114, 178, 0.12); }

  .panel__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: var(--sb-navy);
  }

  .panel__subtitle {
    font-size: 11.5px;
    color: var(--sb-muted);
    margin-top: 2px;
  }

  .panel__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .panel__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 700;
    background: rgba(0, 114, 178, 0.08);
    color: var(--sb-blue);
    flex-shrink: 0;
  }

  /* Header tabel — samakan dengan contoh Nuxt SimpelbangMonthlyTable */
  .table-panel__head {
    align-items: flex-start;
    margin-bottom: 14px;
  }

  .table-panel__title {
    margin: 0;
    font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.6);
    letter-spacing: -0.01em;
  }

  .table-panel__badge {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 6px 10px;
    font-size: 12px;
    font-weight: 700;
    background: rgba(23, 87, 184, 0.1);
    color: #1757b8;
    flex-shrink: 0;
  }

  .chart-stack {
    display: grid;
    grid-template-columns: 1fr;
    gap: 14px;
    margin-bottom: 14px;
  }

  @media (min-width: 1180px) {
    .chart-stack {
      grid-template-columns: 1fr 1fr;
    }
  }

  .chart-loading,
  .chart-empty {
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .hint-text {
    font-size: 12px;
    color: var(--sb-muted);
  }

  /* ===== Tabel ===== */
  .table-wrap {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: 8px;
  }

  .table-wrap::-webkit-scrollbar {
    height: 8px;
  }

  .table-wrap::-webkit-scrollbar-thumb {
    background: #cbd6e2;
    border-radius: 8px;
  }

  .monthly-table {
    width: 100%;
    min-width: 1280px;
    border-collapse: collapse;
    margin-top: 4px;
  }

  .monthly-table th,
  .monthly-table td {
    text-align: left;
    padding: 11px 12px;
    border-bottom: 1px solid #edf1f5;
    font-size: 12.5px;
    color: var(--sb-ink);
    white-space: nowrap;
  }

  .monthly-table th {
    color: var(--sb-muted);
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 700;
    border-bottom: 2px solid var(--sb-border);
    background: #f7f9fc;
  }

  .cell-month {
    font-weight: 700;
    color: var(--sb-navy);
    font-family: 'Plus Jakarta Sans', sans-serif;
  }

  .cell-num {
    font-weight: 500;
  }

  .monthly-table tbody tr:hover {
    background: #f4f8fc;
  }

  .monthly-table tbody tr:nth-child(even) {
    background: #fbfcfe;
  }

  .monthly-table tbody tr:nth-child(even):hover {
    background: #f4f8fc;
  }

  .monthly-table tbody tr.is-current-row td {
    background: rgba(230, 159, 0, 0.07);
  }

  .monthly-table tbody tr.is-current-row .cell-month {
    color: var(--sb-gold);
  }

  .monthly-table th:first-child,
  .monthly-table td:first-child {
    position: sticky;
    left: 0;
    z-index: 2;
    background: #ffffff;
    /* Fix: kolom sticky di dalam tabel yang di-scroll horizontal bisa
       "ditembus" konten yang lewat di baliknya saat scroll aktif (bug
       compositing WebKit/Safari yang cukup umum). Background solid saja
       tidak selalu cukup — dipaksa jadi layer compositing sendiri. */
    will-change: transform;
    transform: translateZ(0);
    isolation: isolate;
  }

  .monthly-table tbody tr:nth-child(even) td:first-child {
    background: #fbfcfe;
  }

  .monthly-table tbody tr:hover td:first-child {
    background: #f4f8fc;
  }

  .monthly-table tbody tr.is-current-row td:first-child {
    /* Padanan solid dari rgba(230, 159, 0, 0.07) di atas putih —
       kolom sticky butuh warna solid, bukan alpha, biar tidak tembus pandang. */
    background: #fdf8ed;
  }

  .monthly-table th:first-child {
    z-index: 3;
    background: #f7f9fc;
  }

  .deviasi-pill {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 20px;
    font-weight: 700;
    font-size: 12px;
  }

  .deviasi-pill--up {
    background: rgba(0, 158, 115, 0.12);
    color: #00714F;
  }

  .deviasi-pill--down {
    background: rgba(213, 94, 0, 0.12);
    color: #954200;
  }

  .deviasi-pill--flat {
    background: #eef1f5;
    color: var(--sb-muted);
  }

  /* ===== Responsif: target khusus laptop 1200-1300px ===== */
  @media (max-width: 1300px) {
    .metric-grid {
      grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
      gap: 12px;
    }

    .sb-content {
      padding-left: 20px;
      padding-right: 20px;
    }

    .month-rail__label {
      font-size: 9.5px;
    }
  }

  @media (max-width: 960px) {
    .metric-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .chart-stack {
      grid-template-columns: 1fr;
    }

    .month-rail {
      grid-template-columns: repeat(6, 1fr);
      row-gap: 12px;
    }
  }

  @media (max-width: 640px) {
    .sb-header__top {
      padding-bottom: 14px;
      align-items: stretch;
    }

    .sb-content {
      padding: 18px 14px 44px;
    }

    .metric-grid {
      grid-template-columns: 1fr;
    }

    .sb-filters {
      width: 100%;
    }

    .sb-filter--year,
    .sb-filter--month {
      width: 100%;
      flex: 1 1 auto;
    }

    .sb-login-btn {
      width: 100%;
      justify-content: center;
    }

    .panel {
      padding: 14px 14px 16px;
    }

    .month-rail {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  </style>

  <!--
    Vuetify men-teleport isi dropdown (v-menu) langsung ke <body>, di luar DOM
    komponen ini, jadi CSS "scoped" di atas tidak selalu bisa menjangkaunya.
    Style global di bawah ini sengaja dijaga hanya aktif selagi halaman
    SIMPELBANG terpasang (lewat class body.sb-page-active yang di-toggle di
    mounted/beforeDestroy), supaya tidak memengaruhi dropdown di halaman lain.
  -->
  <style>
  /* Vuetify set `html { overflow-y: scroll }` — harus di-lock juga,
     bukan cuma body, supaya gutter scrollbar window hilang. */
  html.sb-page-active,
  html.sb-page-active body {
    overflow: hidden !important;
  }

  body.sb-page-active .v-menu__content,
  body.sb-page-active .menuable__content__active {
    z-index: 2600 !important;
  }
  </style>